import { EntityIdType } from '@angular-ru/cdk/entity'
import { inject } from '@angular/core'
import { catchError, map, Observable, of } from 'rxjs'
import { ExtendedEntityCollectionRepositoryOptions } from './extended-entity-collection-repository-options'
import { EntityCollectionRepository } from './entity-collection-repository'
import * as Apollo from 'apollo-angular'
import { QueryOptionsAlone } from 'apollo-angular'
import { OperationVariables } from '@apollo/client'
import { GqlPageInfos, PaginationArgs } from '@wm-front/shared/interfaces'
import { ParallelQueryRunner } from '../graphql/parallel-query-runner'

// eslint-disable-next-line @nx/enforce-module-boundaries

export abstract class EntityCollectionRepositoryManaged<
  Query,
  Entity,
  Body extends OperationVariables,
  K extends number | string = EntityIdType,
  C extends ExtendedEntityCollectionRepositoryOptions = ExtendedEntityCollectionRepositoryOptions
> extends EntityCollectionRepository<Entity, Body, K, C> {
  public gqlQuery: Apollo.Query<Query, Body>

  // Optional so states unit-tested with a mocked GQL service (no Apollo in
  // the TestBed) keep working; without it @parallel splitting is disabled
  private readonly _apollo = inject(Apollo.Apollo, { optional: true })

  /** Fires the split @parallel documents and coordinates deferred merges */
  private readonly _parallelRunner: ParallelQueryRunner<Query, Body, Entity>

  protected constructor(query: Apollo.Query<Query, Body>) {
    super()
    this.gqlQuery = query
    this._parallelRunner = new ParallelQueryRunner<Query, Body, Entity>(
      query,
      this._apollo
    )
  }

  /**
   * Will set the payload
   */
  public setPayload(payload: Body): void {
    const state = this.getState()
    const clone = structuredClone(payload)
    this.setEntitiesState({
      ...state,
      payload: clone,
    })
  }

  public override fetch(
    body: Body,
    mapper?: (source: Query) => Entity[],
    pageMapper?: (source: any) => GqlPageInfos,
    infiniteScrollMode?: boolean,
    context?: QueryOptionsAlone<any>,
    totalCountMapper?: (source: any) => number,
    paginationMapper?: (source: Body) => PaginationArgs,
    additionalInstructions?: (body: Body, response: Query) => any
  ) {
    this.setLoading(true)
    this.setError(null)
    this.setPayload(body)
    const { main$, notifyMainApplied } = this._parallelRunner.fetch(
      body,
      context,
      (source) => (mapper ? mapper(source) : source) as unknown as Entity[],
      (partials) => this._mergeDeferred(partials)
    )
    return main$.pipe(
      map((res) => {
        const data = (mapper ? mapper(res.data) : res.data) as Entity[]
        if (infiniteScrollMode) {
          this.addEntitiesMany(data)
        } else {
          this.setEntitiesAll(data)
        }
        this.setLoading(false)
        if (pageMapper) {
          const pageInfos = pageMapper(res.data)
          this.setPageInfos(pageInfos)
        }
        if (totalCountMapper) {
          this.setTotalCount(totalCountMapper(res.data))
        }
        if (additionalInstructions) {
          additionalInstructions(body, (res as any).data as Query)
        }
        notifyMainApplied()
        return data
      }),
      catchError(async (error) => {
        console.error(error)
        this.setLoading(false)
        this.setError(error)
        return of(error)
      })
    ) as Observable<Entity[]>
  }

  public reload(
    mapper?: (source: any) => Entity[],
    pageMapper?: (source: any) => GqlPageInfos,
    infiniteScrollMode?: boolean,
    context?: QueryOptionsAlone<any>,
    totalCountMapper?: (source: any) => number
  ) {
    const payload = this.getState().payload as Body
    const clone = structuredClone(payload)
    if (payload) {
      return this.fetch(
        clone,
        mapper,
        pageMapper,
        infiniteScrollMode,
        context,
        totalCountMapper
      ).subscribe()
    }
    return undefined
  }

  private _mergeDeferred(partials: Entity[]): void {
    const updates = (partials ?? [])
      .filter((partial) => partial != null)
      .map((partial) => ({
        id: this.selectId(partial),
        changes: partial as Partial<Entity>,
      }))
      .filter((update) => update.id != null)
    if (updates.length) {
      this.updateEntitiesMany(updates)
    }
  }
}
